import { UNIVERSAL_ENV_VARS } from '@config/environmentVariables';
import { getExploreUrl } from '../../views/Explore/context/helpers';

import { safeSlugify } from '@lib/strings/safeSlugify';
import { getDatabaseEnv } from '@lib/environment/getDatabaseEnv';
import { getMykissEnv } from '@lib/environment/getMykissEnv';

const toBoolean = (value: any): boolean => {
  if (value === 'false') {
    return false;
  }

  return !!value;
};

export const getDomain = (): string => {
  switch (getMykissEnv()) {
    case 'production':
      return 'https://fishbrain.com';
    case 'staging':
      if (toBoolean(UNIVERSAL_ENV_VARS.IS_TESTING_DOMAIN)) {
        return 'https://testing.staging.fishbrain.com';
      }
      return 'https://staging.fishbrain.com';
    default:
      return 'http://localhost:3002';
  }
};

export const getAppUrlWithPath = (path: string): string => {
  // Remove leading slash if it exists
  const sanitizedPath = path.startsWith('/') ? path.slice(1) : path;

  switch (getMykissEnv()) {
    case 'production':
      return `fishbrain://${sanitizedPath}`;
    case 'staging':
      return `fishbrain-dev://${sanitizedPath}`;
    default:
      return `fishbrain-dev://${sanitizedPath}`;
  }
};

export const getCognitoDomain = (): string => {
  switch (getDatabaseEnv()) {
    case 'production':
      return 'https://accounts.fishbrain.com';
    default:
      return 'https://accounts.staging.fishbrain.com';
  }
};

// Country / region / city
export const getCountryUrl = (id: string, name: string): string =>
  `/countries/${id.toLowerCase()}-${safeSlugify(name)}`;

export const getRegionUrl = (
  countryId: string,
  regionId: string,
  countryName: string,
  regionName: string,
): string =>
  `/countries/${countryId}-${safeSlugify(countryName)}/regions/${regionId}-${safeSlugify(regionName)}`.toLowerCase();

export const getPlaceUrl = (countryId: string, regionCode: string, slug: string): string =>
  `/cities/${countryId}/${regionCode}/${slug}`.toLowerCase();

export const getFishingWaterUrl = (externalId: string, name?: string | null): string =>
  name
    ? `/fishing-waters/${externalId}/${encodeURIComponent(safeSlugify(name))}`
    : `/fishing-waters/${externalId}`;

export const getPostUrl = (externalId: string): string => `/posts/${externalId}`;

export const getSpeciesUrl = (speciesId: string, name?: string | null): string =>
  `/species/${speciesId}${name ? `/${safeSlugify(name)}` : ''}`;

export const getPageUrl = (pageId: string, name?: string): string =>
  name ? `/pages/${pageId}/${safeSlugify(name)}` : `/pages/${pageId}`;

export const getPageAdminUrl = (pageId: string): string => `/me/pages/${pageId}`;

export const getPageAnalyticsUrl = (pageId: string): string => `/me/pages/${pageId}/analytics`;

export const pathToUrl = (path: string): string => `${getDomain()}${path}`;

export const getUserUrl = (nickame: string): string => `/anglers/${nickame}`;

export const getProUrl = (couponCode?: string): string =>
  couponCode ? `/pro/${couponCode}` : '/pro';

export const getMapWithFishrulesSpeciesUrl = (
  lat: number,
  lng: number,
  fishrulesSpeciesId?: number,
): string => {
  if (!fishrulesSpeciesId) {
    return getExploreUrl({ latitude: lat, longitude: lng, drawerView: 'regulations' });
  }
  return getExploreUrl({
    latitude: lat,
    longitude: lng,
    drawerView: 'regulation',
    drawerViewId: fishrulesSpeciesId.toString(),
  });
};

export const getBlogPostsUrl = (oneIndexedPage?: number, paginationPath?: string): string => {
  if (oneIndexedPage === 1) {
    return paginationPath ?? '/blog';
  }
  if (paginationPath) {
    return oneIndexedPage ? `${paginationPath}/page/${oneIndexedPage}` : paginationPath;
  }
  return oneIndexedPage ? `/blog/page/${oneIndexedPage}` : '/blog';
};

export const getBlogPostUrl = (categorySlug: string, postSlug: string): string =>
  `/blog/${categorySlug}/${postSlug}`;

export const getSpeciesBlogPostsUrl = (speciesSlug: string): string =>
  `/blog/species/${speciesSlug}`;

export const getAuthorBlogPostsUrl = (authorSlug: string): string => `/blog/authors/${authorSlug}`;

export const getTripUrl = (id: string): string => `/trips/${id}`;

export const getSuggestWaterChangesUrl = (waterId: string, waterName: string): string =>
  `/suggest-water-changes?waterId=${waterId}&waterName=${encodeURIComponent(waterName)}`;

export const speciesGroupSlugToSeoId = (speciesGroupSlug: string): string => {
  return `seo_${speciesGroupSlug.replace(/-/g, '_')}`;
};

const seoIdToSpeciesGroupSlug = (seoId: string): string => {
  if (seoId.startsWith('seo_')) {
    return seoId.replace('seo_', '').replace(/_/g, '-');
  }
  throw new Error('Invalid seoId format');
};

export const getSpeciesGroupRegionUrl = (
  countryCode: string,
  regionCode: string,
  speciesGroupSEOId: string,
): string =>
  `/fishing/${countryCode}/${regionCode}/${safeSlugify(seoIdToSpeciesGroupSlug(speciesGroupSEOId))}`.toLowerCase();

export const getSpeciesGroupCountryUrl = (countryCode: string, speciesGroupSEOId: string): string =>
  `/fishing/${countryCode}/${safeSlugify(seoIdToSpeciesGroupSlug(speciesGroupSEOId))}`.toLowerCase();
